import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, forkJoin, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { EnvService } from 'src/app/services/env.service';
import { MainBodyService } from 'src/app/services/main-body.service';
import { SharedService } from 'src/app/services/shared.service';
import { responsedata, themeData } from 'src/interface/themesetting';
import { volume } from 'src/interface/volumeIssue';
import { JournalService } from '../journal.service';
import { VolumeService } from 'src/app/services/volume.service';
import { GetArticlesService } from 'src/app/services/get-articles.service';
import { isPlatformBrowser } from '@angular/common';

@Component({
	selector: 'app-volume-issue-list',
	templateUrl: './volume-issue-list.component.html',
	styleUrls: ['./volume-issue-list.component.scss']
})
export class VolumeIssueListComponent implements OnInit {
	themeSettingsData: themeData;
	journal_id = '';
	subscriptionArr: Subscription[] = [];
	volumesAndIssues: boolean;
	currentArticles: boolean;
	showCurrentArticlesOnly = false;
	volume_id = '';
	isPlatformBrowserStatus = false;
	journalId: string;
	thematicOnly = false;
	volumeData: volume[] = [];
	loading = true;
	volumDataLength = 0;
	volume_heading = ''
	showThematicCollectionLink = false;
	showSupplymentryLink = false;
	constructor(private router: Router,
		private route: ActivatedRoute,
		private sharedService: SharedService,
		private mainBodyService: MainBodyService,
		@Inject(PLATFORM_ID) private platformId: any,
		private titleService: Title,
		private metatag: Meta,
		private EnvService: EnvService,
		private journalService: JournalService,
		private volumeService: VolumeService,
	) { }
	ngOnInit(): void {
		this.volumesAndIssues = this.route.snapshot.url.some(segment => segment.path === 'volumes');
		const formattedStr = this.route.snapshot.url[0].path.split('-') // Split by hyphen
			.map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize first letter of each word
			.join(' ');

		this.titleService.setTitle(formattedStr + ' | ' + 'Journal of British Academy')
		this.metatag.updateTag(
			{ property: 'og:title', content: formattedStr },
		);

		// Verify if you're getting the correct URL
		this.metatag.updateTag(
			{ property: 'og:url', content: this.EnvService.siteUrl + this.route.snapshot.url[0].path },
		);

		this.volume_id = this.route.snapshot.params['volume_id'] != undefined ? this.route.snapshot.params['volume_id'] : this.volume_id;

		const mainSubscription = this.sharedService.getJournalId().pipe(
			switchMap(journalId => {
				this.journal_id = journalId;
				// The original code seemingly always fetched journals because 'this.journalId' was undefined.
				// Preserving the behavior of fetching journals, but now effectively managed via RxJS.
				const journalType = { jnl_type: "JNL" };
				return this.journalService.getJournals(journalType);
			}),
			switchMap((result) => {
				this.journal_id = result.data[0].id;

				this.metatag.updateTag({ property: 'og:type', content: result.data[0].journal_type.type });
				this.metatag.updateTag({ property: 'og:image', content: this.EnvService.imageUrl + 'themes/' + result.data[0].journal_cover_image });

				// Prepare parallel calls
				let volumeObs;
				if (this.thematicOnly) {
					volumeObs = this.volumeService.getThematic(this.journal_id);
				} else {
					volumeObs = this.volumeService.getVolumeIssues(this.journal_id);
				}

				const latestIssueObs = !this.thematicOnly ? this.volumeService.getLatestIssue(this.journal_id) : of(null);
				const themeObs = this.mainBodyService.getAllThemeSettings(this.journal_id);

				return forkJoin({
					volumes: volumeObs,
					latest: latestIssueObs,
					theme: themeObs
				});
			})
		).subscribe(({ volumes, latest, theme }: { volumes: any, latest: any, theme: any }) => {
			// Handle Volumes
			if (volumes.data) {
				volumes.data = volumes.data.map((volume: any) => {
					if (volume.issue_issues && !Array.isArray(volume.issue_issues)) {
						volume.issue_issues = Object.values(volume.issue_issues);
					}
					if (volume.non_issues && !Array.isArray(volume.non_issues)) {
						volume.non_issues = Object.values(volume.non_issues);
					}
					return volume;
				});
			}

			if (this.thematicOnly) {
				this.volumeData = volumes.data;
				this.loading = false;
			} else {
				// Handle Standard Volumes + Latest Issue Logic
				const latestData = latest ? latest.data : null;
				if (this.showCurrentArticlesOnly && latestData) {
					this.volume_heading = 'app_header.current_articles_jba';
					const data = volumes.data.splice(0, 1);
					volumes.data = data.filter((item: any) => {
						item.issue_issues = item.issue_issues.filter((issue: any) => issue.issue === latestData[0]['issue']);
						return item.issue_issues;
					});
					this.loading = false;
				}

				if (this.volume_id != '') {
					this.volumeData = volumes.data.filter((volume: any) => volume.volume === this.volume_id);
					this.volumDataLength = this.volumeData.length;
					this.loading = false;
				} else {
					this.volumeData = volumes.data;
					// console.log(this.volumeData);
					this.volumDataLength = this.volumeData.length;
					this.loading = false;
					this.showThematicCollectionLink = true;
				}
			}

			// Handle Theme
			this.mainBodyService.urlchange(true);
			this.themeSettingsData = theme.data;
			if (isPlatformBrowser(this.platformId)) {
				Object.entries(this.themeSettingsData.theme_colors).map(([key, value]) => {
					const element = "--" + key;
					const val: string = value as string;
					document.documentElement.style.setProperty(element, val);
				});
			}
		});

		this.subscriptionArr.push(mainSubscription);

		if (isPlatformBrowser(this.platformId)) {
			this.isPlatformBrowserStatus = true;
		}
		const url = this.route.params.subscribe(params => {
			this.sharedService.setUrlParams(params);
		});
		this.subscriptionArr.push(url);
	}
	goToPage(url: string) {
		this.router.navigate([url])
	}
	setRegularIssueLink(volume: string, issue: string): any[] {
		return [
			'/articles',
			volume,
			issue,
			'page',
			1
		];
	}

	setCurrentLink(volume: string, issue: string) {
		let url = '';
		if (this.showCurrentArticlesOnly) {
			url += '/current-articles/articles/' + volume + '/' + issue + '/page/' + 1;
		}
		if (!this.showCurrentArticlesOnly) {
			url += '/articles/' + volume + '/' + issue + '/page/' + 1;
		}
		return url;
	}

	setThematicLink(volume: string, issue: string, identifier: string) {
		let url = '';
		// if (this.thematicOnly) {
		url += '/thematic-collections/articles/' + volume + '/' + issue + '/' + identifier + '/page/' + 1;
		// }
		// if (!this.thematicOnly) {
		//     url += '/articles/' + volume + '/' + issue + '/page/' + 1;
		// }
		return url;
	}
	changeIssue(issue: any) {
		// Regular expression pattern to match 'and'
		const pattern = /and/;
		let outputString: string = 'Issue ' + issue;
		if (issue.match(pattern)) {
			// Replace 'and' with '/'
			outputString = 'Issue ' + issue.replace(pattern, ' & ');
		}

		// Regular expression pattern to match 's' followed by a number
		const nextpattern = /s(\d+)/;

		if (issue.match(nextpattern)) {
			const issue_num = issue.match(nextpattern)[1];
			// Replace 's' followed by a number with 'supplementary issue' followed by that number
			outputString = issue.replace(nextpattern, 'Supplementary issue ' + issue_num);

		}

		if (issue == 'non-issue') {
			outputString = 'Issue';
		}

		return outputString;
	}


}
